import { useCallback, useMemo, useEffect } from "react";
import { FINANCE_CONSTANTS } from "@constants/carFinance";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import {
  getRIPPlusConsumer,
  getCarLoanDiscount,
  calculateADPrice,
  calculateCarValue,
  calculateMinDownPayment,
  calculateMaxDownPayment,
  getTotalEmiSavings,
  calculateTotalAckoDriveSavings,
} from "@helpers/carFinanceCalculations";
import {
  validateDownPayment,
  validateInterestRate,
} from "@helpers/carFinanceValidation";
import type { UseCarFinanceLogicProps } from "@interfaces/CarFinance.types";

export const useCarFinanceLogic = ({
  onRoadPrice,
  savingsBreakupData,
  carFinanceData,
  maxSavings,
  initialSelectedBank,
  financeState,
  updateFinanceState,
  updateInputState,
  updateCarLoanDiscount,
  convertAmountToNumber: convertAmountToNumberProp,
}: UseCarFinanceLogicProps) => {
  // Memoized finance slabs
  const financeSlabs = useMemo(
    () =>
      carFinanceData?.car_finance?.ackodrive_finance_saving_slab?.sort(
        (a, b) => a.max_loan_amount - b.max_loan_amount
      ),
    [carFinanceData?.car_finance?.ackodrive_finance_saving_slab]
  );

  // Calculated values using helper functions
  const ADPrice = useMemo(
    () =>
      calculateADPrice(
        onRoadPrice,
        savingsBreakupData,
        financeState.carLoanDiscount
      ),
    [onRoadPrice, savingsBreakupData, financeState.carLoanDiscount]
  );

  const carValue = useMemo(
    () => calculateCarValue(onRoadPrice, savingsBreakupData),
    [onRoadPrice, savingsBreakupData]
  );

  const minDownPayment = useMemo(
    () => calculateMinDownPayment(carValue),
    [carValue]
  );

  const maxDownPayment = useMemo(
    () => calculateMaxDownPayment(carValue),
    [carValue]
  );

  const getTotalEmiSavingsCallback = useCallback(
    () =>
      getTotalEmiSavings(
        onRoadPrice,
        savingsBreakupData,
        financeState.downPayment,
        ADPrice,
        financeState.otherInterest,
        initialSelectedBank,
        financeState.loanTenure
      ),
    [
      onRoadPrice,
      savingsBreakupData,
      financeState.downPayment,
      ADPrice,
      financeState.otherInterest,
      initialSelectedBank,
      financeState.loanTenure,
    ]
  );

  const totalAckoDriveSavings = useMemo(() => {
    const emiSavingsStr = getTotalEmiSavingsCallback();
    return calculateTotalAckoDriveSavings(
      savingsBreakupData,
      financeState.carLoanDiscount,
      emiSavingsStr
    );
  }, [
    savingsBreakupData,
    financeState.carLoanDiscount,
    getTotalEmiSavingsCallback,
  ]);

  // Handle down payment change
  const handleDownPaymentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const raw = e.target.value;
        
        // Clean the input to get only digits
        const cleaned = raw.replace(/[^\d]/g, "");
        const num = Number(cleaned);
        
        // Format the number in Indian currency format
        const formattedValue = cleaned ? getIndianCurrencyFormat(num) : "";
        
        // Update the input value with formatted Indian currency
        updateInputState({
          downPaymentInputValue: formattedValue,
        });

        // Validate the input using the cleaned value
        const validation = validateDownPayment(
          cleaned,
          minDownPayment,
          maxDownPayment,
          carValue
        );
        updateInputState({ downPaymentError: validation.error });
      } catch (error) {
        console.error("Error in down payment input:", error);
        updateInputState({ downPaymentError: "Invalid input format" });
      }
    },
    [
      minDownPayment,
      maxDownPayment,
      carValue,
      updateInputState,
    ]
  );

  // Handle interest change
  const handleInterestChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const val = e.target.value;

        updateInputState({ interestInputValue: val });

        if (val === "") {
          updateFinanceState({ otherInterest: "" });
        } else {
          const numVal = Number(val);
          if (!isNaN(numVal)) {
            updateFinanceState({ otherInterest: numVal });
          }
        }

        const validation = validateInterestRate(val);
        updateInputState({ interestError: validation.error });
      } catch (error) {
        console.error("Error in interest input:", error);
        updateInputState({ interestError: "Invalid input format" });
      }
    },
    [updateInputState, updateFinanceState]
  );

  // Handle modal update
  const handleModalUpdate = useCallback(
    (modalType: string, inputState: any, tempState: any) => {
      try {
        if (modalType === "downpayment") {
          // Clean the formatted input to get the numeric value
          const cleaned = inputState.downPaymentInputValue.replace(/[^\d]/g, "");
          const num = Number(cleaned);
          
          // Validate the final value using the cleaned value
          const validation = validateDownPayment(
            cleaned,
            minDownPayment,
            maxDownPayment,
            carValue
          );
          
          if (!validation.isValid) {
            return false; // Don't update if validation fails
          }
          
          // Update the finance state with the numeric value
          updateFinanceState({
            downPayment: num,
            checkChangeDownpayment: true,
          });
          
          // Keep the formatted value in the input
          updateInputState({
            downPaymentInputValue: getIndianCurrencyFormat(num),
          });
        } else if (modalType === "tenure") {
          updateFinanceState({ loanTenure: tempState.tempLoanTenure });
        } else if (modalType === "interest") {
          const numVal = Number(inputState.interestInputValue);
          if (
            inputState.interestInputValue &&
            (numVal < FINANCE_CONSTANTS.MIN_INTEREST_RATE ||
              numVal > FINANCE_CONSTANTS.MAX_INTEREST_RATE)
          ) {
            return false; // Don't update if validation fails
          }
          updateFinanceState({
            selectedBank: tempState.tempSelectedBank,
            otherInterest: numVal ?? tempState.tempSelectedBank?.interest_rate,
          });
        }
        return true;
      } catch (error) {
        console.error("Error updating values:", error);
        return false;
      }
    },
    [
      minDownPayment,
      maxDownPayment,
      carValue,
      updateFinanceState,
      updateInputState,
    ]
  );

  // Effect for car loan discount and down payment updates
  useEffect(() => {
    try {
      // Calculate car loan discount if needed
      if (
        financeState.iteration < 1 &&
        financeState.downPayment &&
        financeSlabs
      ) {
        const discount = getCarLoanDiscount(
          financeSlabs,
          financeState.downPayment,
          financeState.carLoanDiscount,
          onRoadPrice,
          savingsBreakupData,
          maxSavings
        );
        updateCarLoanDiscount(discount);
        return; // Exit early to avoid down payment calculation on first iteration
      }

      // Only auto-update down payment if user hasn't manually changed it
      if (!financeState.checkChangeDownpayment) {
        // Update down payment based on car loan discount changes
        const basePrice = Math.max(
          0,
          Number(onRoadPrice || 0) -
            getRIPPlusConsumer(savingsBreakupData) -
            financeState.carLoanDiscount -
            (savingsBreakupData?.ackodiscount || 0) -
            Number(savingsBreakupData?.newyear_discount || 0)
        );

        const currentValue =
          basePrice * FINANCE_CONSTANTS.DEFAULT_DOWN_PAYMENT_PERCENTAGE;

        updateFinanceState({ downPayment: currentValue });
        // Don't update input state here to avoid overriding user input
      }
      // If user has manually changed downpayment (checkChangeDownpayment is true),
      // don't override their input - let the validation in handleModalUpdate handle it
    } catch (error) {
      console.error("Error in finance calculations effect:", error);
      // Set fallback values
      const fallbackValue =
        Number(onRoadPrice || 0) *
        FINANCE_CONSTANTS.DEFAULT_DOWN_PAYMENT_PERCENTAGE;
      updateFinanceState({ downPayment: fallbackValue, carLoanDiscount: 0 });
      // Don't update input state in error case to avoid overriding user input
    }
  }, [
    financeState.carLoanDiscount,
    financeState.iteration,
    financeState.checkChangeDownpayment,
    financeSlabs,
    onRoadPrice,
    savingsBreakupData,
    maxSavings,
    updateFinanceState,
    updateCarLoanDiscount,
  ]);

  return {
    // Calculated values
    financeSlabs,
    ADPrice,
    carValue,
    minDownPayment,
    maxDownPayment,
    getTotalEmiSavings: getTotalEmiSavingsCallback,
    totalAckoDriveSavings,
    getRIPPlusConsumer,

    // Handlers
    handleDownPaymentChange,
    handleInterestChange,
    handleModalUpdate,
  };
};
